import { useMemo } from "react";
import {
  authenticateSecurityKey,
  isWebAuthnAvailable,
  stepUpAuthenticate,
} from "../Services/webauthnClient";

type FidoPilotLike = {
  isPilot: boolean;
  hasRegisteredFidoKey: () => boolean;
  settings: {
    credentials: Array<{ id: string; transports?: string[] }>;
    policy?: { allowed?: string[] };
  };
};

type PasswordSubmit = (passwordCombination: string[]) => void | Promise<void>;

interface StepUpOptions {
  transactionId?: string;
  amount?: number;
}

export const FIDO_STEP_UP_TOKEN = "fido-step-up";

export const isFidoStepUpPassword = (
  passwordCombination: string[] | null | undefined
): boolean =>
  Array.isArray(passwordCombination) &&
  passwordCombination.length === 1 &&
  passwordCombination[0] === FIDO_STEP_UP_TOKEN;

const isMockMode = (): boolean =>
  process.env.REACT_APP_FIDO_MOCK === "true";

export const useFidoStepUp = (
  fidoPilot: FidoPilotLike,
  onConfirm: PasswordSubmit,
  stepUpOptions?: StepUpOptions
) =>
  useMemo(() => {
    if (!isWebAuthnAvailable()) return null;
    if (!fidoPilot.isPilot || !fidoPilot.hasRegisteredFidoKey()) return null;

    const policy = fidoPilot.settings.policy;
    const allowsFido =
      policy?.allowed?.includes("fido") ||
      policy?.allowed?.includes("transactional_password");

    if (!allowsFido) return null;

    const credentialRefs = fidoPilot.settings.credentials.map((item) => ({
      id: item.id,
      transports: item.transports,
    }));

    return {
      enabled: true,
      onWebAuthn: async () => {
        if (!isMockMode() && stepUpOptions?.transactionId) {
          await stepUpAuthenticate({
            transactionId: stepUpOptions.transactionId,
            amount: stepUpOptions.amount,
            credentials: credentialRefs,
          });
        } else {
          await authenticateSecurityKey({ credentials: credentialRefs });
        }
      },
      onConfirm: async () => {
        await onConfirm([FIDO_STEP_UP_TOKEN]);
      },
    };
  }, [fidoPilot, onConfirm, stepUpOptions]);
